/**
 * Session Manager Component
 * Handles automatic token refresh and idle timeout logout
 * Requirements: 6.3, 6.6, 12.4, 12.5 - Session management and auto-logout
 */
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useIdleTimeout } from '../hooks/useIdleTimeout';
import { useTokenRefresh } from '../hooks/useTokenRefresh';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

export interface SessionManagerProps {
  children: React.ReactNode;
}

/**
 * SessionManager component that wraps the application to provide:
 * - Automatic token refresh before expiration
 * - Auto-logout after 30 minutes of inactivity
 * - Redirect to login on token expiration
 */
export const SessionManager: React.FC<SessionManagerProps> = ({ children }) => {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  const handleSessionExpired = useCallback(async () => {
    try {
      await logout();
    } catch {
      // Ignore logout errors
    }
    navigate('/login', { replace: true, state: { sessionExpired: true } });
  }, [logout, navigate]);

  const handleIdleTimeout = useCallback(async () => {
    try {
      await logout();
    } catch {
      // Ignore logout errors
    }
    navigate('/login', { replace: true, state: { idleTimeout: true } });
  }, [logout, navigate]);

  // Setup idle timeout (30 minutes of inactivity)
  useIdleTimeout({
    onIdle: handleIdleTimeout,
    timeout: IDLE_TIMEOUT_MS,
    isEnabled: isAuthenticated,
  });

  // Setup automatic token refresh
  useTokenRefresh({
    isAuthenticated,
    onTokenExpired: handleSessionExpired,
  });

  return <>{children}</>;
};

export default SessionManager;
